import { formatDate, StatusField } from '@amfintech/react-admin-ui';
import { createHelpers, createResource } from '@amfintech/refine-react-admin';

import { AdminRole, AdminUserStatus, ModificationEntityType, type AdminUser } from '~/api';
import { RoleList } from '~/pages/edit-role/role-list';

import { resourceNames } from './resource-names';

const { defineFields, defineCardSection, defineShowPage, defineRelatedCardSection } =
  createHelpers<AdminUser>({
    resourceName: resourceNames.adminUser,
  });

const defaultValues = {
  name: '',
  email: '',
  username: '',
  roleId: undefined,
  status: AdminUserStatus.Active,
};

const formatAdminUserStatus = (status: AdminUserStatus) => {
  switch (status) {
    case AdminUserStatus.Active:
      return 'success';
    case AdminUserStatus.Dormant:
      return 'pending';
    case AdminUserStatus.Suspended:
      return 'danger';
    default:
      return 'pending';
  }
};

const filterControls = {
  username: {
    type: 'text',
    config: {
      label: 'LAN ID',
    },
    operator: 'contains',
  },
  name: {
    type: 'text',
    config: {
      label: 'Full Name',
    },
    operator: 'contains',
  },
  email: {
    type: 'text',
    config: {
      label: 'Email',
    },
    operator: 'contains',
  },
  status: {
    type: 'select',
    config: {
      label: 'Status',
      options: Object.values(AdminUserStatus).map((status) => ({
        label: status,
        value: status,
      })),
    },
    operator: 'eq',
  },
} as const;

export const adminUserResource = createResource({
  name: resourceNames.adminUser,
  label: 'Backoffice Users',
  fields: defineFields([
    'id',
    'username',
    'name',
    'email',
    'status',
    'roleId',
    { role: ['name'] },
    'createdAt',
    'updatedAt',
  ]),
  defaultValues,
  confirmBeforeSubmit: true,
  confirmationTitle: 'Backoffice User Confirmation',
  confirmationDescription: 'Please confirm that you want to create this Backoffice User.',
  confirmBeforeEdit: true,
  editConfirmationTitle: 'Backoffice User Update Confirmation',
  editConfirmationDescription: 'Please confirm that you want to update this Backoffice User.',
  controls: {
    sections: [
      {
        title: 'General',
        items: ['username', 'name', 'email', 'status'],
      },
      {
        title: 'User Group',
        items: ['roleId'],
      },
    ],
    components: {
      username: {
        type: 'text',
        config: {
          label: 'LAN ID',
          maxLength: 177,
        },
      },
      email: {
        type: 'text',
        config: {
          label: 'Email',
          type: 'email',
          maxLength: 191,
        },
      },
      name: {
        type: 'text',
        config: {
          label: 'Full Name',
          maxLength: 191,
        },
      },
      roleId: {
        type: 'resource-select',
        config: {
          label: 'User Group',
          resourceName: resourceNames.adminRole,
          optionLabel: 'name',
          optionValue: 'id',
          fields: ['id', 'name'],
        },
      },
      status: {
        type: 'select',
        config: {
          label: 'Status',
          options: Object.values(AdminUserStatus).map((status) => ({
            label: status,
            value: status,
          })),
        },
      },
    },
    componentConfigDefaults: {
      required: true,
    },
  },
  list: {
    filters: {
      collapsible: 'expand-by-default',
      initial: [
        {
          field: 'status',
          operator: 'eq',
          value: AdminUserStatus.Active,
        },
      ],
    },
    downloadConfig: {
      filenamePrefix: 'BackofficeUsers',
    },
    makerChecker: {
      entityTypes: [ModificationEntityType.AdminUser],
      activeTabFilters: [
        {
          field: 'status',
          operator: 'eq',
          value: AdminUserStatus.Active,
        },
      ],
      historyDownloadConfig: {
        fields: [
          'entityType',
          'entityIdentifier',
          { modifyingUser: ['name'] },
          'modifiedBy',
          'modifiedAt',
          { reviewingUser: ['name'] },
          'reviewedAt',
          'type',
          'status',
        ],
        headers: {
          entityType: 'Entity Type',
          entityIdentifier: 'Identifier',
          'modifyingUser.name': 'Modified By',
          modifiedAt: 'Modified At',
          'reviewingUser.name': 'Reviewed By',
          reviewedAt: 'Reviewed At',
          type: 'Type',
          status: 'Status',
        },
        formatters: {
          entityType: () => 'Backoffice User',
          'modifyingUser.name': (value, item) => value || item.modifiedBy || 'SYSTEM',
          modifiedAt: (value) => formatDate(value, { formatType: 'dateAndTime' }),
          reviewedAt: (value) => (value ? formatDate(value, { formatType: 'dateAndTime' }) : ''),
        },
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        filterControls: {
          modifiedAt: {
            type: 'daterange',
            config: {
              label: 'Modified Date',
            },
            operator: 'between',
          },
        },
      },
    },
  },
  defaultPageSize: 10,
  defaultSorter: [{ field: 'createdAt', order: 'desc' }],
  filterControls: filterControls as any,
  allowCreate: true,
  allowEdit: true,
  allowSearch: false,
  allowDownload: true,
  allowDelete: true,
  columns: ({ LinkToDetails, navigateToEdit, allowToEdit }) => [
    {
      id: 'username',
      header: 'LAN ID',
      accessorKey: 'username',
      cell: (data) => {
        const username = data.cell.getValue<string>();

        return (
          <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
            {username}
          </LinkToDetails>
        );
      },
    },
    {
      id: 'name',
      header: 'Full Name',
      accessorKey: 'name',
    },
    {
      id: 'email',
      header: 'Email Address',
      accessorKey: 'email',
    },
    {
      id: 'role.name',
      header: 'User Group',
      accessorKey: 'role.name',
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (data) => {
        const status = data.cell.getValue<AdminUserStatus>();

        return <StatusField status={formatAdminUserStatus(status)} label={status} />;
      },
    },
  ],
  show: defineShowPage({
    sections: [
      defineCardSection({
        title: 'General',
        fields: ['name', 'email', 'username', 'status', { role: ['name'] }],
        collapsible: 'expand-by-default',
        displays: {
          status: {
            label: 'Status',
            render: (data, modifications) => {
              if (!modifications) {
                return <StatusField status={formatAdminUserStatus(data.status)} label={data.status} />;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;

              return <ModificationDiff oldValue={oldValues?.status} newValue={newValues?.status} />;
            },
          },
          'role.name': {
            label: 'User Group',
            render: (data, modifications) => {
              if (!modifications) {
                return <>{data.role?.name}</>;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.name}
                  newValue={modifications.newValues.name}
                  getModifiedResource={{
                    resourceName: resourceNames.adminRole,
                    oldId: modifications.oldValues.name,
                    newId: modifications.newValues.name,
                    field: 'name',
                  }}
                />
              );
            },
          },
        },
      }),
      defineRelatedCardSection<AdminRole>({
        title: 'User Group',
        fields: [{ role: ['id'] }],
        relatedResourceName: resourceNames.adminRole,
        relatedResourceFields: ['name', 'description', 'permissions'],
        relatedResourceFieldName: 'role',
        displays: {
          permissions: {
            label: 'Applied Permissions',
            span: 2,
            render: (resource) => {
              const appliedPermissions = new Set(resource.permissions);

              return (
                <>
                  {appliedPermissions.size > 0 ? (
                    <RoleList
                      appliedPermissions={appliedPermissions}
                      handleAccessChange={() => {}}
                      disabled
                      onlyShowAppliedPermissions
                    />
                  ) : (
                    <div className="text-left text-black text-sm">No permissions applied</div>
                  )}
                </>
              );
            },
          },
        },
      }),
    ],
  }),
});
